import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/lib/pwaInstall"; // capture the install permission before React mounts
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// ---- Force clients onto the newest published build ----
// Independently of the service worker, an already-open/installed app polls the
// build manifest and reloads as soon as a new version is live. In dev mode there
// is no hashed main bundle / manifest, so this safely no-ops.
(function watchForNewBuild() {
  const script = document.querySelector('script[src*="/static/js/main."]');
  if (!script) return; // dev server: nothing to watch
  const currentMain = new URL(script.src).pathname;
  let reloading = false;
  const check = async () => {
    if (reloading) return;
    try {
      const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;
      const manifest = await res.json();
      const latestMain = (manifest.files && manifest.files["main.js"]) || null;
      if (latestMain && latestMain !== currentMain) {
        reloading = true;
        window.location.reload();
      }
    } catch (e) {}
  };
  setInterval(check, 5 * 60 * 1000); // every 5 minutes
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.addEventListener("focus", check);
  check();
})();

// ---- Service worker with FORCED auto-update for installed PWAs ----
// Installed users (esp. iOS) used to stay stuck on an old cached version. We now
// check for a new version on launch + whenever the app returns to foreground,
// and reload automatically the moment a new service worker takes control.
if ("serviceWorker" in navigator) {
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    // Only reload when replacing an existing version (skip the very first install)
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        const check = () => reg.update().catch(() => {});
        check();
        setInterval(check, 60 * 60 * 1000); // hourly
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            // A new version finished installing while a version is already active
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              nw.postMessage && nw.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch(() => {});
  });
}
